import { secureStorage } from './secure-storage';
import { logger } from '@/utils/logger';

/**
 * Storage Migration Utility
 * Migrates data from localStorage to secureStorage with AES-GCM encryption
 */

interface MigrationResult {
  success: boolean;
  migratedKeys: string[];
  errors: string[];
}

/**
 * List of sensitive data keys that should be encrypted
 */
const SENSITIVE_KEYS = [
  'creditCardBills_',
  'billPayments_',
  'futureReleases',
  'debts_',
  'profiles',
  'activeProfileId',
];

/**
 * Checks if a key contains sensitive data
 */
const isSensitiveKey = (key: string): boolean => {
  return SENSITIVE_KEYS.some(pattern => key.includes(pattern));
};

/**
 * Migrates a single item from localStorage to secureStorage
 */
const migrateItem = async (key: string): Promise<boolean> => {
  try {
    const value = localStorage.getItem(key);
    
    if (!value) {
      return false;
    }

    // Parse to verify it's valid JSON
    JSON.parse(value);

    // Store in secureStorage (encrypted)
    await secureStorage.setItem(key, value);

    // Remove from localStorage
    localStorage.removeItem(key);

    logger.info(`[Storage Migration] Migrated: ${key}`);
    return true;
  } catch (error) {
    logger.error(`[Storage Migration] Failed to migrate ${key}:`, error);
    return false;
  }
};

/**
 * Migrates all sensitive data from localStorage to secureStorage
 */
export const migrateToSecureStorage = async (): Promise<MigrationResult> => {
  const result: MigrationResult = {
    success: true,
    migratedKeys: [],
    errors: [],
  };

  try {
    logger.info('[Storage Migration] Starting migration to secure storage...');

    // Get all localStorage keys
    const keys = Object.keys(localStorage);

    // Filter sensitive keys
    const sensitiveKeys = keys.filter(isSensitiveKey);

    if (sensitiveKeys.length === 0) {
      logger.info('[Storage Migration] No sensitive data to migrate');
      return result;
    }

    logger.info(`[Storage Migration] Found ${sensitiveKeys.length} sensitive keys to migrate`);

    // Migrate each key
    for (const key of sensitiveKeys) {
      const success = await migrateItem(key);
      
      if (success) {
        result.migratedKeys.push(key);
      } else {
        result.errors.push(key);
        result.success = false;
      }
    }

    logger.info(`[Storage Migration] Complete. Migrated: ${result.migratedKeys.length}, Errors: ${result.errors.length}`);

    return result;
  } catch (error) {
    logger.error('[Storage Migration] Migration failed:', error);
    result.success = false;
    result.errors.push('General migration error');
    return result;
  }
};

/**
 * Checks if migration has been completed
 */
export const hasMigrationCompleted = (): boolean => {
  const keys = Object.keys(localStorage);
  return !keys.some(isSensitiveKey);
};

/**
 * Gets migration status
 */
export const getMigrationStatus = () => {
  const keys = Object.keys(localStorage);
  const sensitiveKeys = keys.filter(isSensitiveKey);
  
  return {
    needsMigration: sensitiveKeys.length > 0,
    sensitiveKeysCount: sensitiveKeys.length,
    sensitiveKeys,
  };
};
